import React, { useState, useRef } from "react";
import { motion, useInView } from "motion/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

/*
  Usage: pass data from data.json and onBook callback.
  MainStage expects yacht-shaped data (name, images, lengthFt, guests, crew, rating, price, currency, amenities, packages.yachtPass).

  <MainStage data={landingPageData?.MainStage} onBook={() => console.log("Book clicked")} />
*/

const IconLength = () => (
  <svg className="mainstage-stat-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18M3 12h18M3 18h18" />
  </svg>
);
const IconGuests = () => (
  <svg className="mainstage-stat-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconBathrooms = () => (
  <svg className="mainstage-stat-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6h6v12H9V6z" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);
const IconCabins = () => (
  <svg className="mainstage-stat-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 4v16M2 8h20M2 12h20M6 8v8M6 12h12M18 8v8" />
  </svg>
);
const IconTables = () => (
  <svg className="mainstage-stat-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="8" width="18" height="4" rx="0.5" />
    <path d="M4 12v4M8 12v4M12 12v4M16 12v4M20 12v4" />
  </svg>
);

function formatPrice(price, currency) {
  const num = typeof price === "string" ? parseFloat(price) : price;
  const symbol = currency === "THB" || currency == null ? "฿" : currency;
  if (Number.isNaN(num)) return `— ${symbol}`;
  return `${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
}

const DRINK_LABELS = {
  hennessyVSOP: "Hennessy VS",
  changUnpasteurized: "Chang Unpasteurized",
  moet: "Moët",
  donJulio1942: "Don Julio 1942",
  donJulioBlanco: "Don Julio Blanco",
};
function drinkKeyToLabel(key) {
  return DRINK_LABELS[key] ?? key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
}

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.18, delayChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] },
  },
};

/**
 * MainStage – responsive section with header, stats bar, image, tabbed features, price, and CTA.
 * Data comes from data.json (MainStage). Layout order (image then info) is preserved in DOM; stacking on mobile is handled by CSS (single column).
 */
export function MainStage({ data, onBook }) {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0 });

  const d = data ?? {};
  const baseUrl = process.env.PUBLIC_URL || "";

  const subtitle = d.subtitle ?? "MAIN STAGE";
  const title = d.name ?? "";
  const images = Array.isArray(d.images) ? d.images : [];
  const slides = images.length > 0 ? images : [null];
  const priceText = formatPrice(d.price, d.currency);
  const ctaLabel = d.ctaLabel ?? "BOOK NOW";

  const displayStats = [
    { Icon: IconLength, label: "Length", value: d.lengthFt != null ? `${d.lengthFt} ft` : "—" },
    { Icon: IconGuests, label: "Guests", value: d.guests != null ? String(d.guests) : "—" },
    { Icon: IconTables, label: "Tables", value: d.tables != null ? String(d.tables) : "—" },
    { Icon: IconCabins, label: "Cabins", value: d.cabins != null ? String(d.cabins) : "—" },
    { Icon: IconBathrooms, label: "Bathrooms", value: d.bathrooms != null ? String(d.bathrooms) : "—" }
    ,
  ];

  const convenienceFeatures =
    typeof d.amenities === "object" && d.amenities !== null
      ? Object.entries(d.amenities)
          .filter(([, v]) => v === true)
          .map(([k]) => k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim())
      : [];
  const yachtPass = d.packages?.yachtPass;
  const includedDrinks = yachtPass?.includedDrinks;
  const drinkFeatures =
    includedDrinks != null && typeof includedDrinks === "object"
      ? Object.entries(includedDrinks).map(([key, value]) => `${drinkKeyToLabel(key)} x${value}`)
      : [];

  const [activeTab, setActiveTab] = useState("convenience");
  const tabConfig = [
    { key: "convenience", label: "Convenience Feature" },
    { key: "drink", label: "Drink package" },
  ];
  const currentFeatures = activeTab === "convenience" ? convenienceFeatures : drinkFeatures;

  const ITEMS_PER_COLUMN = 7;
  const featureColumns = [];
  for (let i = 0; i < currentFeatures.length; i += ITEMS_PER_COLUMN) {
    featureColumns.push(currentFeatures.slice(i, i + ITEMS_PER_COLUMN));
  }

  if (!data) return null;

  return (
    <div ref={sectionRef} className="mainstage-wrapper">
      <section className="mainstage-section" aria-labelledby="mainstage-title">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          <motion.header className="mainstage-header" variants={itemVariants}>
        {subtitle && (
          <p
            className="mainstage-subtitle"
            style={{
              color: "#183170",
              textAlign: "center",
              fontSize: "24px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "24px",
              letterSpacing: "7.2px",
              textTransform: "uppercase",
            }}
          >
            {subtitle}
          </p>
        )}
        {title && (
          <h2
            className="mainstage-title"
            id="mainstage-title"
            style={{
              color: "var(--Dark-Brown, #321D1D)",
              fontSize: "64px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "88px",
              textTransform: "uppercase",
            }}
          >
            {title}
          </h2>
        )}
        {displayStats.length > 0 && (
          <div className="mainstage-stats mainstage-stats--rows" style={{ fontSize: "18px" }}>
            <div className="mainstage-stats__row mainstage-stats--row">
              {displayStats.slice(0, 3).map((item, i) => (
                <div key={i} className="mainstage-stat-item" aria-label={`${item.label}: ${item.value}`}>
                  <div className="mainstage-stat-item__row">
                    <item.Icon />
                    <span className="mainstage-stat-value" style={{ fontSize: "22px" }}>{item.value}</span>
                  </div>
                  <span className="mainstage-stat-label">{item.label}</span>
                </div>
              ))}
            </div>
            <div className="mainstage-stats__row mainstage-stats--row">
              {displayStats.slice(3, 5).map((item, i) => (
                <div key={3 + i} className="mainstage-stat-item" aria-label={`${item.label}: ${item.value}`}>
                  <div className="mainstage-stat-item__row">
                    <item.Icon />
                    <span className="mainstage-stat-value" style={{ fontSize: "22px" }}>{item.value}</span>
                  </div>
                  <span className="mainstage-stat-label">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
          </motion.header>

          <motion.div className="mainstage-content" variants={itemVariants}>
        <div className="mainstage-image-wrapper">
          <Swiper
            className="mainstage-swiper"
            modules={[Navigation, Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            autoHeight={false}
            navigation
            pagination={{ clickable: true }}
          >
            {slides.map((src, i) => (
              <SwiperSlide key={src ? `${src}-${i}` : `placeholder-${i}`}>
                {src ? (
                  <img
                    src={baseUrl + src}
                    alt={`${title || "Main stage"} – ${i + 1} of ${images.length}`}
                    className="mainstage-image"
                    loading="lazy"
                  />
                ) : (
                  <div className="mainstage-image-placeholder">{title}</div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className="mainstage-info">
          <div className="mainstage-tabs" role="tablist" aria-label="Feature categories" style={{ fontSize: "18px" }}>
            {tabConfig.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeTab === key}
                aria-controls="mainstage-features-panel"
                id={`mainstage-tab-${key}`}
                className={`mainstage-tab ${activeTab === key ? "mainstage-tab--active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {currentFeatures.length > 0 ? (
            <div
              className="mainstage-features-columns"
              id="mainstage-features-panel"
              role="tabpanel"
              aria-labelledby={`mainstage-tab-${activeTab}`}
            >
              {featureColumns.map((columnItems, colIndex) => (
                <ul
                  key={colIndex}
                  className="mainstage-features"
                  style={{ fontSize: "18px", margin: 0, paddingLeft: "1.25em", minWidth: 0 }}
                >
                  {columnItems.map((text, i) => (
                    <li key={colIndex * ITEMS_PER_COLUMN + i}>{text}</li>
                  ))}
                </ul>
              ))}
            </div>
          ) : (
            <ul
              className="mainstage-features"
              id="mainstage-features-panel"
              role="tabpanel"
              aria-labelledby={`mainstage-tab-${activeTab}`}
              style={{ fontSize: "18px" }}
            >
              <li>No features listed.</li>
            </ul>
          )}
          <hr className="mainstage-divider" style={{ border: "none", borderTop: "1px solid currentColor", opacity: 0.2, margin: "1rem 0", width: "100%", display: "block", boxSizing: "border-box" }} aria-hidden="true" />
          <div className="mainstage-price-row">
            <span className="mainstage-price" style={{ fontSize: "32px" }}>{priceText} / Table (8 Passes)</span>
            <a href="https://lin.ee/eTmgxtK" className="mainstage-button" style={{ fontSize: "18px" }}>
              {ctaLabel}
            </a>
          </div>
        </div>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}

export default MainStage;
